import { main_url } from "../pages/basefile";
import { getValidToken } from "../auth/getToken";

async function getAuthHeaders() {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function parseFilename(disposition, fallback) {
  if (!disposition) return fallback;
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

const REPORT_DOWNLOAD_SLUGS = {
  patient: "subject",
};

function defaultReportFilename(reportType, extension) {
  const slug = REPORT_DOWNLOAD_SLUGS[reportType] || reportType;
  return `${slug}-report.${extension}`;
}

function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchReportConfig() {
  const response = await fetch(`${main_url}/api/v1/reports/config`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch report configuration");
  }
  return response.json();
}

export async function previewReport(payload) {
  const response = await fetch(`${main_url}/api/v1/reports/preview`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to preview report");
  }
  return response.json();
}

export async function exportReportCsv(payload) {
  const response = await fetch(`${main_url}/api/v1/reports/export/csv`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to export CSV");
  }
  const blob = await response.blob();
  const filename = parseFilename(response.headers.get("Content-Disposition"), defaultReportFilename(payload.report_type, "csv"));
  triggerDownload(blob, filename);
}

export async function exportReportPdf(payload) {
  const response = await fetch(`${main_url}/api/v1/reports/export/pdf`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to export PDF");
  }
  const blob = await response.blob();
  const filename = parseFilename(response.headers.get("Content-Disposition"), defaultReportFilename(payload.report_type, "pdf"));
  triggerDownload(blob, filename);
}

export async function saveReport(payload) {
  const response = await fetch(`${main_url}/api/v1/reports/save`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to save report");
  }
  return response.json();
}

export async function fetchSavedReports({ page = 0, pageSize = 10 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await fetch(`${main_url}/api/v1/reports/saved?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch saved reports");
  }
  return response.json();
}

export async function syncSavedReport(uid) {
  const response = await fetch(`${main_url}/api/v1/reports/saved/${encodeURIComponent(uid)}/sync`, {
    method: "POST",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to sync saved report");
  }
  return response.json();
}

export async function downloadSavedReportCsv(uid) {
  const response = await fetch(`${main_url}/api/v1/reports/saved/${encodeURIComponent(uid)}/download`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to download saved report CSV");
  }
  const blob = await response.blob();
  const filename = parseFilename(response.headers.get("Content-Disposition"), `${uid}.csv`);
  triggerDownload(blob, filename);
}

export async function downloadSavedReportPdf(uid) {
  const response = await fetch(`${main_url}/api/v1/reports/saved/${encodeURIComponent(uid)}/download/pdf`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to download saved report PDF");
  }
  const blob = await response.blob();
  const filename = parseFilename(response.headers.get("Content-Disposition"), `${uid}.pdf`);
  triggerDownload(blob, filename);
}

/** @deprecated Use downloadSavedReportCsv */
export async function downloadSavedReport(uid) {
  return downloadSavedReportCsv(uid);
}

export async function deleteSavedReport(uid) {
  const response = await fetch(`${main_url}/api/v1/reports/saved/${encodeURIComponent(uid)}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete saved report");
  }
}
